import { Group } from "@fairfleet/geotab";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { AssetFormState } from "../utils/assetEntity";
import AssetCustomFields from "./AssetCustomFields";
import AssetGroupsCard from "./AssetGroupsCard";
import { getErrorMessage } from "@/utilities/error/getErrorMessage";

type Props = {
  form: AssetFormState;
  setField: <K extends keyof AssetFormState>(key: K, value: AssetFormState[K]) => void;
  assetTypeGroups: Group[];
  allGroups: Group[];
  isEditingExisting: boolean;
  deviceTypeLabel: string;
  isFetchingDetails: boolean;
  detailsError: unknown;
  isDetailsError: boolean;
  saveError: unknown;
  isSaveError: boolean;
  showSaveSuccess: boolean;
  isSaving: boolean;
  canSave: boolean;
  onSave: () => void;
};

export default function AssetMasterDataTab({
  form,
  setField,
  assetTypeGroups,
  allGroups,
  isEditingExisting,
  deviceTypeLabel,
  isFetchingDetails,
  detailsError,
  isDetailsError,
  saveError,
  isSaveError,
  showSaveSuccess,
  isSaving,
  canSave,
  onSave,
}: Props) {
  const { t } = useTranslation();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={7}>
        <Paper sx={{ p: 3, height: "100%" }}>
          <Stack spacing={2.5}>
            <Typography variant="h6">{t("assets.masterData")}</Typography>

            {isFetchingDetails && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="body2">{t("assets.loadingDetails")}</Typography>
              </Stack>
            )}
            {isDetailsError && <Alert severity="error">{getErrorMessage(detailsError) || t("assets.loadDetailsError")}</Alert>}

            <TextField
              label={t("assets.fields.name")}
              size="small"
              value={form.name}
              onChange={event => setField("name", event.target.value)}
              required
            />

            <TextField
              label={t("assets.fields.comment")}
              size="small"
              value={form.comment}
              onChange={event => setField("comment", event.target.value)}
              multiline
              minRows={3}
              maxRows={10}
              sx={{ "& textarea": { resize: "vertical" } }}
            />

            <TextField
              label={t("assets.fields.licensePlate")}
              size="small"
              value={form.licensePlate}
              onChange={event => setField("licensePlate", event.target.value)}
            />

            <TextField
              label={t("assets.fields.vin")}
              size="small"
              value={form.vehicleIdentificationNumber}
              onChange={event => setField("vehicleIdentificationNumber", event.target.value)}
            />

            <TextField
              label={t("assets.serialNumberLabel")}
              size="small"
              value={form.serialNumber}
              onChange={event => setField("serialNumber", event.target.value)}
              required
              // An existing asset's serial changes only through "Seriennummer tauschen".
              slotProps={{ input: { readOnly: isEditingExisting } }}
            />

            {isEditingExisting && (
              <TextField
                label={t("assets.deviceTypeLabel")}
                size="small"
                value={deviceTypeLabel}
                slotProps={{ input: { readOnly: true } }}
              />
            )}

            <Divider />
            <Typography variant="subtitle1">{t("assets.customFieldsTitle")}</Typography>

            <AssetCustomFields
              customProperties={form.customProperties}
              onChange={next => setField("customProperties", next)}
            />

            {isSaveError && (
              <Alert severity="error">{getErrorMessage(saveError) || t("assets.saveError")}</Alert>
            )}

            {showSaveSuccess && <Alert severity="success">{t("assets.saveSuccess")}</Alert>}

            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button variant="contained" onClick={onSave} disabled={!canSave}>
                {isEditingExisting ? t("assets.save") : t("assets.createAsset")}
              </Button>
              {isSaving && <CircularProgress size={20} />}
            </Stack>
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12} md={5}>
        <Stack spacing={2}>
          <AssetGroupsCard
            assetTypeGroups={assetTypeGroups}
            allGroups={allGroups}
            assetTypeGroup={form.assetTypeGroup}
            groups={form.groups}
            onAssetTypeGroupChange={group => setField("assetTypeGroup", group)}
            onGroupsChange={groups => setField("groups", groups)}
          />
        </Stack>
      </Grid>
    </Grid>
  );
}
